import { Dispatch, SetStateAction } from 'react'
import * as d3 from 'd3'
import dayjs from 'dayjs'
import {
  fillDatesBetweenRangeStrings,
  findDateRangeForDate,
  getCustomDatesForStripe,
  getCustomDatesForTimePeriod,
  getUtcDateAndTime,
  sortLabels
} from '../../../../library/utils'
import { dateFormat } from 'lib/utils'
import {
  ChartDataExtended,
  CPIFrequency,
  CPITimePeriod,
  CustomLabelDate,
  DataStatus,
  Label,
  LabelCount
} from 'models'
import { ADD_EVENT_BUTTON_X_OFFSET, appendButtonAddEvent, appendButtonCountIndicator } from '.'

interface Props {
  svg: d3.Selection<SVGSVGElement | null, unknown, null, undefined>
  uniqueMonths: string[]
  pinnedLabels?: Label[]
  selectedTimePeriod: CPITimePeriod
  selectedFrequency: CPIFrequency
  filteredData: any[]
  groupedByWeeks: ChartDataExtended | null
  dataDates: LabelCount[]
  height: number
  stripeWidth: number
  setDrawerOpen: Dispatch<SetStateAction<boolean>>
  setMode: Dispatch<SetStateAction<string>>
  setCustomDate: ({ date, start, end }: CustomLabelDate) => void
  setRedirectToMode: Dispatch<SetStateAction<string>>
  dataStartsAt: string
  status: DataStatus | undefined
}

export const appendEventIndicators = ({
  svg,
  uniqueMonths,
  pinnedLabels,
  selectedTimePeriod,
  selectedFrequency,
  filteredData,
  groupedByWeeks,
  dataDates,
  height,
  stripeWidth,
  setDrawerOpen,
  setMode,
  setCustomDate,
  setRedirectToMode,
  dataStartsAt,
  status
}: Props) => {
  const eventIndicators = svg.selectAll('.event-indicator-count')
  if (!eventIndicators.empty()) eventIndicators.remove()
  const addCustomEventButtons = svg.selectAll('.add-event-button')
  if (!addCustomEventButtons.empty()) addCustomEventButtons.remove()

  /* Render Event Indicators */
  uniqueMonths.forEach((date, index) => {
    const addEventButton = svg.append('g').attr('class', 'add-event-button')
    const addEventButtonTooltip = svg.append('g').attr('class', 'add-button-tooltip')

    if (pinnedLabels) {
      pinnedLabels.sort(sortLabels)
    }
    const thisDate = getUtcDateAndTime(date)
    const thisDateFormatted = dayjs(thisDate.labelFormatDate).format(dateFormat.customLabel)
    const dateRangeForDate = findDateRangeForDate(
      thisDateFormatted,
      selectedTimePeriod,
      selectedFrequency,
      groupedByWeeks ? groupedByWeeks.values : filteredData
    )
    const filledDates = fillDatesBetweenRangeStrings(dateRangeForDate.start, dateRangeForDate.end).map((n) => {
      const d = getUtcDateAndTime(n)

      return d.labelFormatDate
    })
    const filteredFilledDates = dataDates.filter((n) => filledDates.includes(n.date)).map((n) => n.count)
    /* ONYXIA-6303 */
    const foundDataDate = dataDates.find((d) => {
      const labelDate = d.date.slice(0, 10) // Convert "2025-05-31T00:00:00.000Z" to "2025-05-31"
      return labelDate === date || labelDate === thisDateFormatted
    })
    const dateRangeCount = filteredFilledDates.length > 0 ? filteredFilledDates.reduce((a, b) => a + b) : 0
    let indicatorValue = 0

    if (selectedTimePeriod === CPITimePeriod.Week && foundDataDate) {
      indicatorValue = foundDataDate.count
      /* ONYXIA-6303 */
    } else if (selectedTimePeriod === CPITimePeriod.Custom) {
      // For custom periods, use direct date matching first, then fall back to range
      indicatorValue = dateRangeCount || 0
      // if (foundDataDate) {
      //   indicatorValue = foundDataDate.count
      // } else {
      //   indicatorValue = dateRangeCount || 0
      // }
    } else {
      indicatorValue = dateRangeCount || 0
    }

    appendButtonAddEvent({ addEventButton, addEventButtonTooltip, height })

    if (dataDates.some((n) => n.count > 0) && status !== DataStatus.Initializing) {
      const countIndicatorButton = svg.append('g').attr('class', 'event-indicator-count')
      const countIndicatorButtonTooltip = svg.append('g').attr('class', 'count-tooltip')

      appendButtonCountIndicator({
        countIndicatorButton,
        pinnedLabels,
        selectedTimePeriod,
        filledDates,
        indicatorValue,
        stripeWidth,
        index,
        height,
        date: thisDateFormatted
      })

      countIndicatorButton.on('mousemove', () => {
        const text = `Label${indicatorValue === 1 ? '' : 's'}`
        countIndicatorButtonTooltip.style('display', 'block')
        svg.select('.count-rect-tooltip-wrapper').style('display', 'block')
        svg.select('.count-rect-tooltip-text').style('display', 'block').text(`${indicatorValue} ${text}`)
      })
      countIndicatorButton.on('mouseout', () => {
        countIndicatorButtonTooltip.style('display', 'none')
        svg.select('.count-rect-tooltip-wrapper').style('display', 'none')
        svg.select('.count-rect-tooltip-text').style('display', 'none')
      })
      /* This badge belongs to one stripe and counts that stripe's range, so it opens on that range. */
      countIndicatorButton.on('click', () => {
        setDrawerOpen(true)
        setMode('show-date')
        const { start, end } = getCustomDatesForStripe(
          date,
          selectedTimePeriod,
          selectedFrequency,
          groupedByWeeks ? groupedByWeeks.values : filteredData,
          dataStartsAt
        )
        setCustomDate({ date, start, end })
        setRedirectToMode('show-date')
      })
    }

    addEventButton.on('mousemove', () => {
      addEventButtonTooltip.style('display', 'block')
    })
    addEventButton.on('mouseout', () => {
      addEventButtonTooltip.style('display', 'none')
    })
    /*
     * Unlike the badge, this button follows the cursor across stripes, so the stripe it stands on has
     * to come from where it was clicked - in the svg's own coordinates, undoing the shift it is drawn
     * with, since a viewport x would need correcting for wherever the chart sits on the page.
     * */
    addEventButton.on('click', (e) => {
      setDrawerOpen(true)
      setMode('create')
      const [pointerX] = d3.pointer(e, svg.node())
      const {
        date: stripeDate,
        start,
        end
      } = getCustomDatesForTimePeriod(
        pointerX - ADD_EVENT_BUTTON_X_OFFSET,
        stripeWidth,
        selectedTimePeriod,
        selectedFrequency,
        uniqueMonths,
        groupedByWeeks ? groupedByWeeks.values : filteredData,
        dataStartsAt
      )
      setCustomDate({ date: stripeDate, start, end })
      setRedirectToMode('show-date')
    })
  })
}
